import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { MapPin } from 'lucide-react';
import LocationPicker from './LocationPicker';

export default function EditRideModal({ ride, onClose, onUpdated }) {
  const [loading, setLoading] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(
    ride.latitude && ride.longitude ? {
      lat: ride.latitude,
      lng: ride.longitude,
      address: ride.formatted_address || ride.location || ''
    } : null
  );
  const [formData, setFormData] = useState({
    date: ride.date || '',
    time: ride.time || '',
    distance: ride.distance?.toString() || '',
    category: ride.category || '',
    meeting_point: ride.location || '',
    route_description: ride.route_description || '',
    route_link: ride.route_link || '',
    max_riders: ride.max_riders?.toString() || ''
  });

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = {
      date: formData.date,
      time: formData.time,
      distance: parseInt(formData.distance),
      category: formData.category,
      latitude: selectedLocation?.lat || ride.latitude,
      longitude: selectedLocation?.lng || ride.longitude,
      formatted_address: selectedLocation?.address || ride.formatted_address || '',
      location: formData.meeting_point,
      route_description: formData.route_description,
      route_link: formData.route_link,
      max_riders: formData.max_riders ? parseInt(formData.max_riders) : null
    };

    try {
      await axios.put(`/rides/${ride.id}`, data);
      toast.success('Ride updated successfully!');
      onUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update ride');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!showLocationPicker && (
        <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Ride</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => updateFormField('date', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="time">Time</Label>
                  <Input
                    id="time"
                    type="time"
                    required
                    value={formData.time}
                    onChange={(e) => updateFormField('time', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="distance">Distance (km)</Label>
                  <Input
                    id="distance"
                    type="number"
                    required
                    min="5"
                    max="500"
                    value={formData.distance}
                    onChange={(e) => updateFormField('distance', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="max_riders">Max Riders</Label>
                  <Input
                    id="max_riders"
                    type="number"
                    min="1"
                    max="50"
                    placeholder="No limit"
                    value={formData.max_riders}
                    onChange={(e) => updateFormField('max_riders', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="category">Ride Type</Label>
                <select
                  id="category"
                  required
                  value={formData.category}
                  onChange={(e) => updateFormField('category', e.target.value)}
                  className="w-full p-2 rounded-md bg-gray-800 border border-gray-700 text-white"
                >
                  <option value="">Select ride type...</option>
                  <option value="road">Road</option>
                  <option value="mountain">Mountain</option>
                  <option value="gravel">Gravel</option>
                  <option value="commute">Commute</option>
                </select>
              </div>

              <div>
                <Label>Start Location</Label>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-1 justify-start text-left h-auto py-3"
                  onClick={() => setShowLocationPicker(true)}
                >
                  <MapPin className="w-4 h-4 mr-2 flex-shrink-0 text-purple-500" />
                  {selectedLocation ? (
                    <span className="truncate text-foreground">{selectedLocation.address}</span>
                  ) : (
                    <span className="text-muted-foreground">Tap to change location</span>
                  )}
                </Button>
              </div>

              <div>
                <Label htmlFor="meeting_point">Meeting Point Details</Label>
                <Input
                  id="meeting_point"
                  placeholder="e.g., Car park entrance"
                  value={formData.meeting_point}
                  onChange={(e) => updateFormField('meeting_point', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="route_description">Route Description</Label>
                <Textarea
                  id="route_description"
                  required
                  rows={2}
                  value={formData.route_description}
                  onChange={(e) => updateFormField('route_description', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="route_link">Route Link</Label>
                <Input
                  id="route_link"
                  type="url"
                  placeholder="Strava, Garmin link"
                  value={formData.route_link}
                  onChange={(e) => updateFormField('route_link', e.target.value)}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={handleLocationSelect}
        initialLocation={selectedLocation}
      />
    </>
  );
}
